import * as pdfjsLib from 'pdfjs-dist';
import { GlobalWorkerOptions } from 'pdfjs-dist';

// Set up the worker for PDF.js - use local worker file
GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

// REACT_APP_API_ENDPOINT -- the name every other file uses and the only one the silo builds set.
// This file read REACT_APP_API_URL, which is set nowhere, so it always fell through to the hardcoded
// fallback below and sent PDF conversion requests to the DEMO silo from whichever silo the user was in.
// Browser CORS blocked it, so nothing crossed; but in EET that would have been a UK->US data-residency
// breach had the origin ever been allowed. Found 30 Sept from a console log during a TEST upload.
const API_BASE_URL = process.env.REACT_APP_API_ENDPOINT || 'https://fg0jf2txt9.execute-api.us-east-2.amazonaws.com/prod';

export interface ConvertedImage {
  blob: Blob;
  pageNumber: number;
  fileName: string;
}

/**
 * Convert a PDF file to PNG images (one per page)
 * @param file - The PDF file to convert
 * @param getIdTokenClaims - Function to get auth token claims
 * @param quality - JPEG quality (0.0 to 1.0), default 0.95
 * @returns Array of converted images with metadata
 */
export type PdfProcessingMode = 'auto' | 'multi' | 'single';

export async function convertPdfToImages(
  file: File,
  getIdTokenClaims: () => Promise<any>,
  quality: number = 0.95,
  mode: PdfProcessingMode = 'multi'
): Promise<ConvertedImage[]> {
  try {
    const fileSizeMB = file.size / 1024 / 1024;
    console.log(`[PDF Converter] File "${file.name}" is ${fileSizeMB.toFixed(2)} MB`);

    // ALWAYS convert via S3. The direct /convert-pdf path renders every page
    // synchronously and times out on page-heavy PDFs (which can be small in bytes,
    // e.g. a 75-page scan). The S3 path uploads first (no 6MB API Gateway body limit)
    // and the backend routes small/few-page PDFs to a fast sync convert and
    // large/many-page PDFs to async (pages land straight in the processing pipeline).
    console.log(`[PDF Converter] Using S3-based conversion`);
    return await convertPdfToImagesViaS3(file, getIdTokenClaims, quality, mode);

    // eslint-disable-next-line no-unreachable
    console.log(`[PDF Converter] Using direct server-side conversion`);

    // Convert file to base64 for server upload
    const arrayBuffer = await file.arrayBuffer();
    const base64Data = btoa(
      new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
    );

    // Get auth token
    const tokenClaims = await getIdTokenClaims();
    const token = tokenClaims?.__raw;

    // Call the server API for conversion
    console.log(`[PDF Converter] Sending to server for conversion...`);
    const response = await fetch(`${API_BASE_URL}/convert-pdf`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        pdfData: `data:application/pdf;base64,${base64Data}`,
        filename: file.name,
        dpi: 300  // Use 300 DPI for good quality
      })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || `Server conversion failed: ${response.status}`);
    }

    const result = await response.json();

    if (!result.success || !result.pages) {
      throw new Error('Invalid response from server');
    }

    console.log(`[PDF Converter] Server conversion successful: ${result.pages.length} pages`);

    // Convert the server response to our ConvertedImage format
    const images: ConvertedImage[] = [];

    for (const page of result.pages) {
      console.log(`[PDF Converter] Processing page ${page.pageNumber}...`);

      // Convert data URL back to blob
      const dataUrlResponse = await fetch(page.dataUrl);
      const blob = await dataUrlResponse.blob();

      images.push({
        blob,
        pageNumber: page.pageNumber,
        fileName: page.fileName
      });

      console.log(`[PDF Converter] Page ${page.pageNumber} processed: ${page.width}x${page.height} (${(page.sizeBytes / 1024).toFixed(1)}KB)`);
    }

    console.log(`Successfully converted ${images.length} pages to images`);
    return images;

  } catch (error) {
    console.error('Error converting PDF to images:', error);
    throw error;
  }
}

/**
 * Check if a file is a PDF
 */
export function isPdfFile(file: File): boolean {
  return file.type === 'application/pdf' ||
         file.name.toLowerCase().endsWith('.pdf');
}

/**
 * Convert PDF to images via S3 for large files
 */
async function convertPdfToImagesViaS3(
  file: File,
  getIdTokenClaims: () => Promise<any>,
  quality: number = 0.95,
  mode: PdfProcessingMode = 'multi'
): Promise<ConvertedImage[]> {
  try {
    console.log(`[PDF S3 Converter] Starting S3-based conversion for "${file.name}"`);

    // Get auth token
    const tokenClaims = await getIdTokenClaims();
    const token = tokenClaims?.__raw;

    // Step 1: Get presigned URL for S3 upload
    console.log(`[PDF S3 Converter] Getting S3 upload URL...`);
    const uploadUrlResponse = await fetch(`${API_BASE_URL}/convert-pdf-s3`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        action: 'get_upload_url',
        filename: file.name,
        fileSize: file.size
      })
    });

    if (!uploadUrlResponse.ok) {
      const error = await uploadUrlResponse.json().catch(() => ({}));
      throw new Error(error.error || `Failed to get upload URL: ${uploadUrlResponse.status}`);
    }

    const uploadData = await uploadUrlResponse.json();
    console.log(`[PDF S3 Converter] Got S3 upload URL:`, uploadData.uploadUrl);
    console.log(`[PDF S3 Converter] Presigned POST fields:`, uploadData.fields);

    // Step 2: Upload PDF to S3
    const formData = new FormData();

    // Add all the fields from the presigned POST (must be added BEFORE the file)
    Object.entries(uploadData.fields).forEach(([key, value]) => {
      console.log(`[PDF S3 Converter] Adding field: ${key} = ${value}`);
      formData.append(key, value as string);
    });

    // Add the file last (important for S3)
    formData.append('file', file);

    const uploadResponse = await fetch(uploadData.uploadUrl, {
      method: 'POST',
      body: formData
    });

    if (!uploadResponse.ok) {
      // Try to get error details from S3
      let errorDetail = '';
      try {
        const errorText = await uploadResponse.text();
        console.error(`[PDF S3 Converter] S3 upload error response:`, errorText);
        // Parse XML error if present
        if (errorText.includes('<Error>')) {
          const codeMatch = errorText.match(/<Code>([^<]+)<\/Code>/);
          const messageMatch = errorText.match(/<Message>([^<]+)<\/Message>/);
          if (codeMatch) errorDetail += ` Code: ${codeMatch[1]}`;
          if (messageMatch) errorDetail += ` Message: ${messageMatch[1]}`;
        }
      } catch (e) {
        console.error(`[PDF S3 Converter] Could not parse error response`);
      }
      throw new Error(`S3 upload failed: ${uploadResponse.status}${errorDetail}`);
    }

    console.log(`[PDF S3 Converter] Upload complete, triggering conversion...`);

    // Step 3: Trigger conversion from S3
    const convertResponse = await fetch(`${API_BASE_URL}/convert-pdf-s3`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        action: 'convert',
        s3Key: uploadData.s3Key,
        dpi: 300,
        // 'auto': the backend samples the first pages and routes the PDF down the SINGLE or
        // MULTI extraction path itself; 'multi': rasterize every page (legacy behaviour).
        processingMode: mode
      })
    });

    if (!convertResponse.ok) {
      const error = await convertResponse.json().catch(() => ({}));
      throw new Error(error.error || `Conversion failed: ${convertResponse.status}`);
    }

    const result = await convertResponse.json();

    // Check if this is an async conversion
    if (result.async && result.jobId) {
      console.log(`[PDF S3 Converter] Large PDF detected, async conversion started. Job ID: ${result.jobId}`);
      console.log(`[PDF S3 Converter] Estimated time: ${result.estimatedTime} seconds`);

      // Return a special response for async processing
      // We return empty images array but with a success flag
      return [{
        blob: new Blob(['async'], { type: 'text/plain' }),
        pageNumber: 0,
        fileName: 'async_processing',
        asyncProcessing: true,
        message: mode === 'auto'
          ? `✅ PDF received. Cal is checking whether it is one report or several and will process it accordingly — results will appear in the system within a few minutes.`
          : `✅ Large PDF is being processed! Your ${Math.ceil(result.estimatedTime / 3)}-page document has been queued for processing and will appear in the system within a few minutes.`,
        jobId: result.jobId,
        estimatedTime: result.estimatedTime
      } as any];
    }

    if (!result.success || !result.pages) {
      throw new Error('Invalid response from server');
    }

    console.log(`[PDF S3 Converter] Conversion successful: ${result.pages.length} pages`);

    // Convert the server response to our ConvertedImage format
    const images: ConvertedImage[] = [];

    for (const page of result.pages) {
      console.log(`[PDF S3 Converter] Processing page ${page.pageNumber}...`);

      // Convert data URL back to blob
      const dataUrlResponse = await fetch(page.dataUrl);
      const blob = await dataUrlResponse.blob();

      images.push({
        blob,
        pageNumber: page.pageNumber,
        fileName: page.fileName
      });
    }

    console.log(`[PDF S3 Converter] Successfully processed ${images.length} pages`);
    return images;

  } catch (error) {
    console.error('[PDF S3 Converter] Conversion failed:', error);
    throw error;
  }
}

/**
 * Convert blob to base64 data URL for WebSocket transmission
 */
export async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result as string);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Convert PDF to base64 images for WebSocket batch upload
 */
export async function convertPdfToBase64Images(
  file: File,
  getIdTokenClaims: () => Promise<any>,
  quality: number = 0.95,
  mode: PdfProcessingMode = 'multi'
): Promise<Array<{fileName: string, data: string, pageNumber: number}>> {
  try {
    console.log(`Converting PDF "${file.name}" to base64 images for batch upload...`);

    // First convert to blobs
    const images = await convertPdfToImages(file, getIdTokenClaims, quality, mode);

    // Then convert each blob to base64
    const base64Images: Array<{fileName: string, data: string, pageNumber: number}> = [];

    for (const image of images) {
      // Preserve the async-processing marker so the caller detects it and skips
      // uploading (the converter already dropped the real pages into incoming/).
      // Without this the 5-byte 'async' marker blob gets uploaded as a junk page.
      if ((image as any).asyncProcessing) {
        base64Images.push({
          fileName: image.fileName,
          data: '',
          pageNumber: image.pageNumber,
          asyncProcessing: true,
          message: (image as any).message,
          jobId: (image as any).jobId,
          estimatedTime: (image as any).estimatedTime,
        } as any);
        continue;
      }
      const base64Data = await blobToBase64(image.blob);
      base64Images.push({
        fileName: image.fileName,
        data: base64Data,
        pageNumber: image.pageNumber
      });
    }

    console.log(`Successfully converted ${base64Images.length} pages to base64 images`);
    return base64Images;

  } catch (error) {
    console.error('Error converting PDF to base64 images:', error);
    throw error;
  }
}